/**
 * English locale catalog for the player-filters sub-domain
 * (`apps/frontend/src/features/gvg-planner/components/player-filters`).
 *
 * Each constant below is the canonical English shape; the matching Russian
 * catalog in `./ru.ts` must satisfy `typeof <enName>` exactly. Consumers pair
 * the catalog with `defineLocaleCatalog({ en, ru })` from `@/shared/i18n` and
 * resolve it through `useLocaleMessages(catalog)`.
 *
 * English is the source-of-truth shape — when a key disappears from `en`,
 * `ru` must drop the matching key or the type system will fail.
 *
 * Translation scope: this catalogue covers the BOARD-VIEW filter UI, the
 * GROUP-ROSTER selector, and the TAG-ASSIGNMENT selector — every string is
 * system chrome that surrounds user-created tags, player names, weapon
 * names, group labels, and room names. Those user/persisted values pass
 * through as arguments to the message functions and are NOT translated
 * here. `tag.name`, `group.label`, `player.inGameName`, `weapon.name` and
 * `room.name` are persisted data.
 */
export const playerFiltersEn = {
  controls: {
    modeHeading: 'Board behavior',
    modeDescription:
      'Choose whether non-matching players disappear or stay visible but muted.',
    modeGroupLabel: 'Player filter behavior',
    modeFilter: 'Filter',
    modeHighlight: 'Highlight',
    nameHeading: 'Player name',
    nameDescription: 'Searches both in-game and Discord names.',
    nameInputLabel: 'Player name',
    nameInputPlaceholder: 'Type a player name',
    weaponsHeading: 'Weapons',
    weaponsDescription:
      'Matches the visible current build. Other player and guild builds are listed separately.',
    tagsHeading: 'Tags',
    tagsDescription: 'Matches any selected guild tag.',
    noGuildTags: 'This guild has no player tags yet.',
    clearFilters: 'Clear filters',
  },
  drawer: {
    eyebrow: {
      tag: 'Tag assignment',
      group: 'Group roster',
      board: 'Board view',
    },
    status: {
      matchCount: (matching: number, total: number) => `${matching} of ${total} players match`,
      hintNoFilters: 'Add a name, weapon, or tag to change the board.',
      hintOtherBuilds:
        'The board matches the visible current build. Players with these weapons only in another build are listed below.',
      hintFilterMode:
        'Only matching players are shown. Drag-and-drop is paused while hidden players remain in group order.',
      hintHighlightMode: 'Matching players stay at full contrast; all other cards are muted.',
    },
    selectorHeading: (target: string) => `Select players for ${target}`,
    selectorHintTag: 'Checking assigns this tag; unchecking removes it from the player.',
    selectorHintGroup:
      'Unselecting returns a signed-up player to Signed and everyone else to Other.',
    selectorMissingTag: 'This tag is no longer available. Close the drawer and try again.',
    selectorMissingGroup: 'This group is no longer available. Close the drawer and try again.',
    titleFallback: 'Player filters',
    titleForTag: (name: string) => `${name} · Players`,
    titleForGroup: (name: string) => `${name} · Players`,
    currentBuildHeading: 'Current build',
    currentBuildDescription: 'Players currently showing any selected weapon on their card.',
    otherBuildsHeading: 'Other player or guild builds',
    otherBuildsDescription: 'These players have the selected weapons outside their current build.',
    matchingHeading: 'Matching players',
    resultCountSingular: (count: number) => `${count} result`,
    resultCountPlural: (count: number) => `${count} results`,
  },
  resultList: {
    emptyCurrentWeapon: 'No players use the selected weapons in their current build.',
    emptyOtherBuildWeapon:
      'No additional players have the selected weapons in another player or guild build.',
    emptyNoFilters: 'No players match these filters.',
    listLabelCurrentBuildSelector: (target: string) =>
      `Current-build players available for ${target}`,
    listLabelCurrentBuildFilter: 'Players matching in their current build',
    listLabelOtherBuildSelector: (target: string) =>
      `Other-build players available for ${target}`,
    listLabelOtherBuildFilter: 'Players matching in another player or guild build',
    listLabelSelectorOnly: (target: string) => `Players available for ${target}`,
    sourceLabelPlayerBuild: 'Player build',
    sourceLabelGuildBuild: 'Guild build',
    buildChoiceGuild: (index: number) => `Guild ${String(index + 1)}`,
    buildChoicePlayerDefault: 'Player 1 · Default',
    buildChoicePlayer: (index: number) => `Player ${String(index + 1)}`,
    preferredBuildBadge: 'Will use',
    willUseChoiceHint: 'Preferred choice for this assignment.',
    noWeapons: 'No weapons',
    overflowTags: (extra: number) => `+${String(extra)}`,
    fallbackGroupUnassigned: 'Unassigned',
    /** Singular fallback used when a player has no other identifiable group. */
    fallbackGroupLabel: (index: number) => `Group ${String(index + 1)}`,
    groupLabelWithRoom: (room: string, group: string) => `${room} · ${group}`,
    playerToggleTitlePending: 'Waiting for the authoritative board update',
    playerToggleTitleFallbackSigned: 'This is the signed-up player fallback group',
    playerToggleTitleFallbackOther: 'This is the not-signed-up player fallback group',
    pendingStatusTag: 'Updating tag…',
    pendingStatusMove: 'Moving…',
    rowTitleCurrent: (player: string) => `${player} · Current build`,
    rowTitleLabels: (player: string, labels: string) => `${player} · ${labels}`,
    /** `props.targetGroupLabel` / `props.targetTagLabel` defaults. */
    fallbackGroupLabelProp: 'group',
    fallbackTagLabelProp: 'tag',
    playerToggleAriaAssignTag: (tagLabel: string, playerName: string) =>
      `Assign ${tagLabel} to ${playerName}`,
    playerToggleAriaRemoveTag: (tagLabel: string, playerName: string) =>
      `Remove ${tagLabel} from ${playerName}`,
    playerToggleAriaMoveToGroup: (playerName: string, groupLabel: string) =>
      `Move ${playerName} to ${groupLabel}`,
    playerToggleAriaRemoveFromGroup: (playerName: string, groupLabel: string) =>
      `Remove ${playerName} from ${groupLabel}`,
  },
}